import createAsyncGetters from '../config/asyncGetters/createAsyncGetters';
import { AtoneServiceType } from './iap.ruleset';
import { AtoneServiceType as ReplicantAtoneServiceType } from '@play-co/replicant';
import { assertNever } from '../utils';
import {
  cancelPendingPaymentAsync,
  getPendingPaymentsStatusAsync,
  getPendingPaymentStatusAsync,
} from './iap.pending.payments';

export const iapAsyncGetters = createAsyncGetters({
  generateAtonePaymentChecksum: (
    paymentData: { [key: string]: unknown },
    api,
  ) => {
    // For local debug only
    if (process.env.REPLICANT_OFFLINE) {
      const hash = async (string: string) => {
        const msgBuffer = new TextEncoder().encode(string);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return btoa(String.fromCharCode(...hashArray));
      };

      const getSortedValues = (input: unknown) => {
        const result: unknown[] = [];

        const keysToExclude = [
          'checksum',
          'transaction_options',
          'shop_transaction_no',
        ];

        if (Array.isArray(input)) {
          for (const val of input) {
            result.push(...getSortedValues(val));
          }
        } else if (input && typeof input === 'object') {
          const keys = Object.keys(input).sort();

          for (const key of keys) {
            if (keysToExclude.includes(key)) {
              continue;
            }

            const val = input[key as keyof typeof input];

            result.push(...getSortedValues(val));
          }
        } else {
          result.push(input);
        }

        return result;
      };

      if (!process.env.ATONE_SECRET_KEY) {
        throw new Error('ATONE_SECRET_KEY is not set for your env');
      }

      const sortedValues = getSortedValues(paymentData).join('');
      const hashInput = `${process.env.ATONE_SECRET_KEY},${sortedValues}`;

      return hash(hashInput);
    }

    return api.payments.generateAtonePaymentChecksum(paymentData);
  },

  getAtoneTransaction: (
    transaction: { id: string; type: AtoneServiceType },
    api,
  ) => {
    let type: ReplicantAtoneServiceType;
    switch (transaction.type) {
      case AtoneServiceType.ATONE:
        type = 'atoneNextMonthPayment';
        break;
      case AtoneServiceType.ATONE_LITE:
        type = 'atoneTsudoDeferredPayment';
        break;
      default:
        assertNever(transaction.type);
    }
    return api.payments.getAtoneTransaction(transaction.id, type);
  },

  getStripePaymentIntentStatus: (args: { paymentIntentId: string }, api) =>
    api.payments.getPaymentStatus(args),

  getKomojuPayment: (args: string, api) => api.payments.getKomojuPayment(args),

  /**
   * Get all pending payments and check their actual statuses
   * Its return only payments not claimed by the user.
   */
  getPendingPaymentsStatus: (args: { limit: number }, api) =>
    getPendingPaymentsStatusAsync(args, api),

  /**
   * Get actual pending payment status
   */
  getPendingPaymentStatus: (args: { pendingPaymentId: string }, api) =>
    getPendingPaymentStatusAsync(args, api),

  /**
   * Try to cancel pending payment on platform
   * TODO: Why the cancelKomojuPayment exist only at asyncGetters api?
   */
  cancelPendingPayment: (args: { pendingPaymentId: string }, api) =>
    cancelPendingPaymentAsync(args, api),
});
